import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState, useCallback } from "react";

interface Variant {
  price: number;
}

interface Excursion {
  slug: string;
  title: string;
  subtitle: string;
  category: string;
  categoryKey: string;
  duration: string;
  rating: number;
  reviewsCount: number;
  priceFrom: number;
  heroImage: string;
  gallery?: string[];
  pickup?: boolean;
  wheelchair?: boolean;
  location: string;
  variants: Variant[];
  imageFilter?: string;
}

interface Props {
  excursion: Excursion;
}

export default function ExcursionCard({ excursion }: Props) {
  const { t } = useTranslation();
  const [fallbackIndex, setFallbackIndex] = useState(-1);

  // Build fallback chain: hero -> gallery[0] -> gallery[1] -> ...
  const imageSources = [excursion.heroImage];
  if (excursion.gallery && excursion.gallery.length > 0) {
    imageSources.push(...excursion.gallery);
  }

  const currentSrc = imageSources[fallbackIndex + 1] ?? null;

  const handleError = useCallback(() => {
    setFallbackIndex((prev) => prev + 1);
  }, []);

  return (
    <Link
      to={`/excursion/${excursion.slug}`}
      data-product-shop
      className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-accent-300 transition-all duration-300 flex flex-col cursor-pointer"
    >
      <div className="relative w-full aspect-[16/10] overflow-hidden bg-gradient-to-br from-background-200 via-background-150 to-background-200">
        {currentSrc === null ? (
          <div className="w-full h-full flex flex-col items-center justify-center text-foreground-400">
            <div className="w-14 h-14 flex items-center justify-center mb-2">
              <i className="ri-image-line text-4xl"></i>
            </div>
            <span className="text-xs">{t(`ex_title_${excursion.slug}`)}</span>
          </div>
        ) : (
          <img
            src={currentSrc}
            alt={t(`ex_title_${excursion.slug}`)}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            style={excursion.imageFilter ? { filter: excursion.imageFilter } : undefined}
            onError={handleError}
          />
        )}
        <div className="absolute top-3 left-3 bg-background-50/95 backdrop-blur px-2.5 py-1 rounded-full text-[11px] font-medium text-foreground-800 flex items-center gap-1">
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-map-pin-2-line text-primary-500"></i>
          </div>
          {excursion.location.split(",")[0]}
        </div>
        <div className="absolute top-3 right-3 bg-accent-500 text-background-50 px-2.5 py-1 rounded-full text-[11px] font-semibold">
          {t(excursion.categoryKey).split("&")[0].trim()}
        </div>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-1 mb-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="w-4 h-4 flex items-center justify-center">
              <i
                className={(i <= Math.floor(excursion.rating) ? "ri-star-fill" : "ri-star-line") + " text-accent-500 text-sm"}
              ></i>
            </div>
          ))}
          <span className="text-xs text-foreground-600 ml-1">
            {excursion.rating} · {excursion.reviewsCount} {t("ex_reviews_word")}
          </span>
        </div>

        <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 leading-snug mb-1.5 group-hover:text-accent-700 transition-colors">
          {t(`ex_title_${excursion.slug}`)}
        </h3>
        <p className="text-sm text-foreground-600 leading-relaxed mb-4 line-clamp-2">
          {t(`ex_subtitle_${excursion.slug}`)}
        </p>

        <div className="flex flex-wrap gap-2 mb-5">
          <span className="inline-flex items-center gap-1 bg-accent-100 text-accent-900 text-[11px] px-2.5 py-1 rounded-full">
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-time-line"></i>
            </div>
            {t(`ex_dur_${excursion.slug}`)}
          </span>
          {excursion.pickup && (
            <span className="inline-flex items-center gap-1 bg-secondary-100 text-secondary-900 text-[11px] px-2.5 py-1 rounded-full">
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-bus-2-line"></i>
              </div>
              {t("el_pickup")}
            </span>
          )}
          {excursion.wheelchair && (
            <span className="inline-flex items-center gap-1 bg-secondary-100 text-secondary-900 text-[11px] px-2.5 py-1 rounded-full">
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-wheelchair-line"></i>
              </div>
              {t("el_accessible")}
            </span>
          )}
        </div>

        <div className="mt-auto flex items-end justify-between pt-4 border-t border-background-200/70">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-foreground-500">{t("el_from")}</div>
            <div className="font-heading text-2xl font-semibold text-accent-700">
              €{excursion.priceFrom}
            </div>
          </div>
          <div className="text-sm font-medium text-foreground-800 flex items-center gap-1 group-hover:text-accent-700 whitespace-nowrap">
            {t("el_view_details")}
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"></i>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}